import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { defaultExamContext } from "@features/exam/contexts/constants";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import type { FeedbackParams } from "@memorang/features/src/feedback/types";
import Constants from "expo-constants";
import { usePathname } from "expo-router";
import { useMemo } from "react";
import { Platform } from "react-native";

type UseFeedbackParamsOptions = {
	view?: string;
	type?: "Review" | "Content Flagged";
	itemId?: string;
	sessionId?: string;
	threadId?: string;
	runId?: string;
	featureName?: string;
	sessionType?: string;
	rating?: string;
	itemVersionId?: string;
};

export const useFeedbackParams = (
	options: UseFeedbackParamsOptions = {},
): FeedbackParams => {
	const pathname = usePathname();
	const { email, username } = useAuthContext();
	const companyName = useAppStore((store) => store.companyName);
	const appName = useAppStore((store) => store.app.name);
	const { currentExamContext } = useExamContext();

	const {
		projectId,
		organizationId,
		examName,
		studyPackProductNumericId,
		studyPackCollectionId,
	} = currentExamContext.projectId ? currentExamContext : defaultExamContext;

	const {
		view,
		type,
		itemId,
		sessionId,
		threadId,
		runId,
		rating,
		featureName,
		sessionType,
	} = options;

	return useMemo(
		() => ({
			email: email ?? "",
			userName: username ?? "",
			appName,
			platform: Platform.OS,
			appLanguage: i18n.language,
			currentAppVersion: Constants.expoConfig?.version,
			view: view ?? pathname,
			type: (type as "Review" | "Content Flagged") ?? ("Review" as const),
			companyName,
			bundleName: examName,
			context: examName,
			projectId,
			organizationId,
			productId: studyPackProductNumericId,
			collectionId: studyPackCollectionId,
			...(itemId && {
				itemId,
				itemVersionId: "1",
				sessionType: sessionType ?? "FORMATIVE",
			}),
			...(sessionId && { sessionId }),
			...(threadId && { threadId }),
			...(runId && { runId }),
			...(rating && {
				rating: rating as "positive" | "negative",
			}),
			...(featureName && { feature: featureName }),
		}),
		[
			email,
			username,
			appName,
			pathname,
			companyName,
			examName,
			projectId,
			organizationId,
			studyPackProductNumericId,
			studyPackCollectionId,
			view,
			type,
			itemId,
			sessionId,
			threadId,
			runId,
			rating,
			featureName,
			sessionType,
		],
	);
};
